import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Toaster,
  useToastController,
  Toast,
  ToastTitle, mergeClasses
} from "@fluentui/react-components";
import {
  DocumentAdd20Regular,
  Building20Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import StepHero from "../components/kennzahlenSectionStep/StepHero";
import YearScrollGrid from "../components/kennzahlenSectionStep/YearScrollGrid";
import {
  KENNZAHLEN_SECTIONS,
  TOPLEVEL_FIELD_KEYS,
  type FieldDef,
} from "../components/kennzahlenSectionStep/sections";
import {
  brancheZeilenMergen,
  wizardBetraegeLesen,
  wizardBetraegeSchreiben,
  wizardBrancheKey,
  type BrancheKatalogEintrag,
} from "../components/kennzahlen/revBranchen";
import { activeGruppen, brancheGruppenLaden, type BrancheGruppe } from "../data/brancheGruppen";
import { branchengruppenLaden } from "../services/branchengruppeService";
import { SearchableSelect, type SelectOption } from "../components/forms/widgets/SearchableSelect";

const DRAFT_LS_PREFIX = "kennzahlen-section-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;

// Answers: flat map "{fieldKey}_{year}" -> string
type Answers = Record<string, string>;

const useStyles = makeStyles({
  page: {
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "24px",
    paddingRight: "24px",
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    marginLeft: "-24px",
    marginRight: "-24px",
    paddingTop: "16px",
    paddingBottom: "14px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "#f8f8f8",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    ...shorthands.borderBottom("1px", "solid", "rgba(45,20,43,0.08)"),
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    marginTop: "auto",
    marginLeft: "-24px",
    marginRight: "-24px",
    backgroundColor: BRAND.white,
    ...shorthands.borderTop("1px", "solid", "rgba(45,20,43,0.1)"),
    boxShadow: "0 -6px 18px rgba(45,20,43,0.06)",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: BRAND.ink,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  titleStack: { display: "flex", flexDirection: "column", rowGap: "6px", minWidth: 0 },
  titleChips: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  titleChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: BRAND.ink,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("999px"),
    lineHeight: 1.2,
  },
  titleChipIcon: { display: "inline-flex", alignItems: "center", color: BRAND.muted },
  formPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  heroBox: { flexShrink: 0 },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "0",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  sectionTabs: { display: "flex", flexWrap: "wrap", columnGap: "8px", rowGap: "8px" },
  sectionTab: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  sectionTabActive: { color: BRAND.white },
  contextPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    flexDirection: "row",
    columnGap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  contextBlock: { display: "flex", flexDirection: "column", rowGap: "2px", minWidth: "180px" },
  contextLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  contextName: { fontFamily: BRAND.fontAccent, fontSize: "1.15rem", color: BRAND.ink },
  contextSub: { fontSize: "0.85rem", color: BRAND.muted },
  yearRange: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    backgroundColor: BRAND.white,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.85rem",
    color: BRAND.ink,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
  },
  yearsPromptIntro: {
    fontSize: "0.95rem",
    color: BRAND.muted,
    marginTop: 0,
    marginBottom: "16px",
  },
  yearsTileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  yearsTile: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    fontWeight: 500,
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "4px",
    textAlign: "center",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, background-color, transform, box-shadow",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
      boxShadow: "0 6px 14px rgba(45,20,43,0.08)",
    },
  },
  yearsTileActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  yearsTileNum: { fontFamily: BRAND.fontAccent, fontSize: "1.6rem", fontWeight: 600 },
  yearsTileLabel: { fontSize: "0.78rem", color: "inherit", opacity: 0.85 },
  yearsRangePreview: {
    marginTop: "8px",
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  questionsBlock: {
    marginTop: "20px",
    paddingTop: "16px",
    ...shorthands.borderTop("1px", "dashed", "rgba(45,20,43,0.15)"),
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  questionsBlockTitle: {
    margin: 0,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.ink,
  },
  questionRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  questionLabel: {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: BRAND.ink,
  },
  yesNoRow: {
    display: "inline-flex",
    columnGap: "8px",
  },
  yesNoBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  yesNoBtnActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  questionTextarea: {
    width: "100%",
    minHeight: "70px",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("6px"),
    outlineWidth: 0,
    resize: "vertical",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
  questionInput: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("6px"),
    outlineWidth: 0,
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
  questionSubFields: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  questionSubField: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  brancheAuswahl: {
    marginTop: "12px",
    maxWidth: "360px",
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  questionSubLabel: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────

const parseNumberRaw = (raw: string): number | undefined => {
  if (!raw) return undefined;
  const cleaned = String(raw)
    .replace(/[^\d,.\-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
};

const ansKey = (fieldKey: string, year: number) => `${fieldKey}_${year}`;

type KPIYear = {
  year: number;
  revenueTotal?: number;
  cogsEur?: number;
  grossProfit?: number;
  avgConcurrentPersons?: number;
  otherInfo?: string;
};

/** Alle Feld-Keys, die dieser Wizard selbst verwaltet (Gesamt + Details je Sektion). */
const WIZARD_FIELD_KEYS = new Set<string>(
  KENNZAHLEN_SECTIONS.flatMap((sec) =>
    [sec.gesamt, ...sec.details].map((f) => f.key),
  ),
);

/**
 * Build KPIYear payload from answers for a single year.
 *
 * `preservedOtherInfo` = das beim Laden dekodierte `otherInfo` dieses Jahres.
 * `otherInfo` ist im gespeicherten Datensatz EIN einzelner (JSON-kodierter) Key —
 * das Backend merged nur auf oberster Ebene, ersetzt diesen Key also komplett.
 * Ohne Durchreichen würde jedes Speichern aus diesem Wizard alle Keys löschen, die
 * er nicht kennt (u. a. `leiharbeiter_*`, `subunternehmer_*`, `freelancer_*` sowie
 * die Zusatzfragen — nur im Kennzahlen-Reiter erfassbar).
 */
export const buildPayloadForYear = (
  answers: Answers,
  year: number,
  preservedOtherInfo?: Record<string, unknown>,
  branchen?: { ids: readonly number[]; katalog: readonly BrancheKatalogEintrag[] },
): KPIYear => {
  const out: KPIYear = { year };
  const extra: Record<string, unknown> = {};
  // Fremde Keys zuerst — die vom Wizard verwalteten Felder überschreiben sie unten.
  // Bewusst gefiltert statt nur „vorher spreaden": sonst käme ein hier geleertes
  // Feld (leerer String → nicht im Payload) über den alten Wert wieder zurück.
  for (const [k, v] of Object.entries(preservedOtherInfo ?? {})) {
    if (WIZARD_FIELD_KEYS.has(k) || TOPLEVEL_FIELD_KEYS.has(k)) continue;
    if (v === undefined || v === null || v === "") continue;
    extra[k] = v;
  }
  for (const sec of KENNZAHLEN_SECTIONS) {
    const fields: FieldDef[] = [sec.gesamt, ...sec.details];
    for (const f of fields) {
      const raw = answers[ansKey(f.key, year)] ?? "";
      if (raw.trim() === "") continue;
      if (TOPLEVEL_FIELD_KEYS.has(f.key)) {
        const num = parseNumberRaw(raw);
        if (num !== undefined) {
          (out as any)[f.key] = num;
        }
      } else {
        extra[f.key] = raw;
      }
    }
  }
  // Umsatzverteilung je Branchengruppe: über dasselbe Format wie der
  // Kennzahlen-Reiter (`otherInfo.revBranchen`). Nur Gruppen, deren Antwort in
  // diesem Jahr überhaupt existiert — eine nie berührte Zeile schreibt nichts.
  let extraMitBranchen: Record<string, unknown> = extra;
  if (branchen && branchen.ids.length > 0) {
    const betraege = new Map<number, string>();
    for (const id of branchen.ids) {
      const k = ansKey(wizardBrancheKey(id), year);
      if (k in answers) betraege.set(id, answers[k] ?? "");
    }
    extraMitBranchen = wizardBetraegeSchreiben(extra, betraege, branchen.katalog);
  }
  if (Object.keys(extraMitBranchen).length > 0) out.otherInfo = JSON.stringify(extraMitBranchen);
  return out;
};

/**
 * Decode persisted KPIYear back into Answers entries for the given year.
 * `otherInfoByYear` sammelt das rohe `otherInfo`-Objekt je Jahr, damit fremde Keys
 * beim Speichern wieder mitgeschrieben werden können (siehe buildPayloadForYear).
 */
export const decodeKpiYear = (
  kpi: KPIYear,
  into: Answers,
  otherInfoByYear?: Record<number, Record<string, unknown>>,
  katalog: readonly BrancheKatalogEintrag[] = [],
) => {
  const { year, otherInfo, ...top } = kpi;
  for (const [k, v] of Object.entries(top)) {
    if (v === undefined || v === null) continue;
    into[ansKey(k, year)] = String(v);
  }
  if (otherInfo) {
    try {
      const parsed = JSON.parse(otherInfo) as Record<string, unknown>;
      if (otherInfoByYear) otherInfoByYear[year] = parsed;
      for (const [k, v] of Object.entries(parsed)) {
        if (v === undefined || v === null || v === "") continue;
        // Objekte/Listen (revBranchen, Ja/Nein-Felder des Reiters) sind keine
        // Wizard-Antworten — als String stünde dort „[object Object]".
        if (typeof v === "object") continue;
        into[ansKey(k, year)] = String(v);
      }
      // Branchen-Beträge (neues Format + Altwerte) unter dem Wizard-Schlüssel.
      for (const [id, betrag] of wizardBetraegeLesen(parsed, katalog)) {
        into[ansKey(wizardBrancheKey(id), year)] = betrag;
      }
    } catch {
      // ignore malformed
    }
  }
};

/** Branchengruppen-Ids, zu denen `answers` einen nicht-leeren Betrag trägt. */
const branchenIdsAusAntworten = (answers: Answers): number[] => {
  const ids = new Set<number>();
  for (const [k, v] of Object.entries(answers)) {
    const m = /^revBranche_(\d+)_\d{4}$/.exec(k);
    if (m && v.trim() !== "") ids.add(Number(m[1]));
  }
  return [...ids];
};

// ── Component ─────────────────────────────────────────────────────────────

export type KennzahlenSectionStepFormEmbedded = {
  contactId: number;
  onSuccess?: () => void;
};

export type KennzahlenSectionStepFormProps = {
  embedded?: KennzahlenSectionStepFormEmbedded;
};

const KennzahlenSectionStepForm = ({ embedded }: KennzahlenSectionStepFormProps = {}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { contactId: contactIdParam } = useParams();
  const contactId = useMemo<number | null>(() => {
    if (embedded?.contactId) return embedded.contactId;
    const n = Number(contactIdParam ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [contactIdParam, embedded?.contactId]);

  const isEmbedded = Boolean(embedded);

  const toasterId = "kennzahlen-section-toaster";
  const { dispatchToast } = useToastController(toasterId);

  const [answers, setAnswers] = useState<Answers>({});
  const [yearsBack, setYearsBack] = useState<number | null>(null);
  const [inYearsStep, setInYearsStep] = useState<boolean>(true);
  const [years, setYears] = useState<number[]>(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  });

  // Whenever yearsBack changes, derive the years range:
  // [y - yearsBack, … , y - 1, y, y + 1]. Plan-Jahr (current+1) ist immer dabei.
  useEffect(() => {
    if (yearsBack === null) return;
    const y = new Date().getFullYear();
    const range: number[] = [];
    for (let i = yearsBack; i >= 1; i -= 1) range.push(y - i);
    range.push(y, y + 1);
    setYears(range);
  }, [yearsBack]);
  const [contactName, setContactName] = useState<string>("");
  const [contactAddr, setContactAddr] = useState<string>("");
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  const sections = KENNZAHLEN_SECTIONS;
  const draftKey = useMemo(
    () => `${DRAFT_LS_PREFIX}${contactId ?? "new"}`,
    [contactId],
  );

  // Beim Laden dekodiertes `otherInfo` je Jahr — wird beim Speichern unverändert
  // durchgereicht, damit die nur im Kennzahlen-Reiter erfassbaren Keys erhalten
  // bleiben. Jahre ohne geladene Daten fehlen hier schlicht (→ undefined).
  const loadedOtherInfoRef = useRef<Record<number, Record<string, unknown>>>({});

  // Umsatzverteilung je Branchengruppe: Branchen der Firma + gespeicherte +
  // per „+ Branche" hinzugefügte (siehe `kennzahlen/revBranchen.ts`).
  const [brancheKatalog, setBrancheKatalog] = useState<BrancheGruppe[]>([]);
  const [firmaBranchenIds, setFirmaBranchenIds] = useState<number[]>([]);
  const [gespeichertBranchenIds, setGespeichertBranchenIds] = useState<number[]>([]);
  const [hinzugefuegtBranchenIds, setHinzugefuegtBranchenIds] = useState<number[]>([]);
  const branchenZeilen = useMemo(
    () =>
      brancheZeilenMergen({
        firmenGruppenIds: firmaBranchenIds,
        gespeichertIds: gespeichertBranchenIds,
        // Auch Gruppen aus einem wiederhergestellten Entwurf: deren Beträge
        // stehen nur in `answers` — ohne Zeile gingen sie beim Speichern verloren.
        hinzugefuegtIds: [...hinzugefuegtBranchenIds, ...branchenIdsAusAntworten(answers)],
        katalog: brancheKatalog,
      }),
    [firmaBranchenIds, gespeichertBranchenIds, hinzugefuegtBranchenIds, brancheKatalog, answers],
  );
  const branchenFelder = useMemo<FieldDef[]>(
    () =>
      branchenZeilen.map((z) => ({
        key: wizardBrancheKey(z.branchengruppeId),
        label: `Branche: ${z.label}${z.zugeordnet ? "" : " (nicht in der Akte)"} (€)`,
        type: "euro" as const,
        indent: 1 as const,
      })),
    [branchenZeilen],
  );
  const branchenOptionen = useMemo<SelectOption[]>(() => {
    const belegt = new Set(branchenZeilen.map((z) => z.branchengruppeId));
    return activeGruppen(brancheKatalog)
      .filter((g) => !belegt.has(g.id))
      .map((g) => ({ value: String(g.id), label: g.ebene2, hint: g.ebene1 }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [branchenZeilen, brancheKatalog]);

  // Load contact name + existing KPI data
  useEffect(() => {
    if (!contactId) return;
    let aborted = false;
    (async () => {
      try {
        const [cRes, kRes, katalogRows, firmaGruppen] = await Promise.all([
          fetch(apiUrl(`contacts/${contactId}`)),
          fetch(apiUrl(`risk-kpi/${contactId}`)),
          brancheGruppenLaden(),
          // Ohne Akte-Branchen geht es weiter — gespeicherte Werte stehen trotzdem da.
          branchengruppenLaden(contactId).catch(() => []),
        ]);
        if (aborted) return;
        setBrancheKatalog(katalogRows);
        setFirmaBranchenIds(firmaGruppen.map((z) => z.branchengruppeId));
        if (!aborted && cRes.ok) {
          const c = await cRes.json();
          const name =
            c?.displayName ||
            [c?.firstName, c?.lastname ?? c?.lastName].filter(Boolean).join(" ") ||
            c?.companyName ||
            `Kontakt ${contactId}`;
          setContactName(name);
          const a = c?.defaultAddress ?? {};
          const parts = [
            a.street,
            [a.postalCode, a.city].filter(Boolean).join(" "),
            a.country,
          ].filter(Boolean) as string[];
          setContactAddr(parts.join(", "));
        }
        if (!aborted && kRes.ok) {
          const items: KPIYear[] = await kRes.json();
          if (!Array.isArray(items)) return;
          const next: Answers = {};
          const otherInfoByYear: Record<number, Record<string, unknown>> = {};
          for (const k of items) {
            if (k && typeof k.year === "number") {
              decodeKpiYear(k, next, otherInfoByYear, katalogRows);
            }
          }
          const gespeichert = new Set<number>();
          for (const oi of Object.values(otherInfoByYear)) {
            for (const id of wizardBetraegeLesen(oi, katalogRows).keys()) gespeichert.add(id);
          }
          setGespeichertBranchenIds([...gespeichert]);
          loadedOtherInfoRef.current = otherInfoByYear;
          setAnswers((prev) => ({ ...next, ...prev }));
        }
      } catch (err) {
        console.error("Kennzahlen load failed", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [contactId]);

  // Restore draft once (after load)
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        if (parsed.answers && typeof parsed.answers === "object") {
          setAnswers((prev) => ({ ...prev, ...parsed.answers }));
        }
        if (Array.isArray(parsed.years) && parsed.years.length > 0) {
          setYears(parsed.years.map((n: any) => Number(n)).filter(Number.isFinite));
        }
        if (typeof parsed.yearsBack === "number") {
          setYearsBack(parsed.yearsBack);
          setInYearsStep(false);
        }
        if (typeof parsed.currentSectionIdx === "number") {
          setCurrentSectionIdx(
            Math.min(Math.max(0, parsed.currentSectionIdx), sections.length - 1),
          );
        }
        if (typeof parsed.savedAt === "string") setDraftSavedAt(parsed.savedAt);
      }
    } catch {
      // ignore malformed
    }
  }, [draftKey, sections.length]);

  // Autosave draft
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ answers, years, yearsBack, currentSectionIdx, savedAt }),
        );
        setDraftSavedAt(savedAt);
      } catch {
        // ignore
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [answers, years, currentSectionIdx, draftKey]);

  // Jahre-Step ist Step 0; danach kommen die N Sektionen → Gesamt N+1.
  const totalSteps = sections.length + 1;
  const stepIndex = inYearsStep ? 0 : currentSectionIdx + 1;
  const progressFraction = totalSteps > 1 ? stepIndex / (totalSteps - 1) : 0;
  const isSubmitStep = !inYearsStep && currentSectionIdx === sections.length - 1;
  const canPrev = !inYearsStep;
  const canNext = inYearsStep ? yearsBack !== null : true;

  const goPrev = () => {
    if (inYearsStep) return;
    if (currentSectionIdx === 0) {
      setInYearsStep(true);
      return;
    }
    setCurrentSectionIdx((i) => Math.max(0, i - 1));
  };
  const goNext = () => {
    if (inYearsStep) {
      if (yearsBack === null) return;
      setInYearsStep(false);
      setCurrentSectionIdx(0);
      return;
    }
    setCurrentSectionIdx((i) => Math.min(sections.length - 1, i + 1));
  };

  const saveDraftNow = () => {
    setSavingDraft(true);
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ answers, years, currentSectionIdx, savedAt }),
      );
      setDraftSavedAt(savedAt);
    } catch {
      // ignore
    } finally {
      setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const shiftYears = (delta: number) =>
    setYears((prev) => prev.map((y) => y + delta));

  const getValue = (fieldKey: string, year: number) =>
    answers[ansKey(fieldKey, year)] ?? "";
  const setValue = (fieldKey: string, year: number, value: string) =>
    setAnswers((prev) => ({ ...prev, [ansKey(fieldKey, year)]: value }));

  const onSubmit = async () => {
    if (!contactId) {
      dispatchToast(
        <Toast>
          <ToastTitle>Kein Kontakt-Kontext — Speichern nicht möglich</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all(
        years.map(async (yr) => {
          const payload = buildPayloadForYear(answers, yr, loadedOtherInfoRef.current[yr], {
            ids: branchenZeilen.map((z) => z.branchengruppeId),
            katalog: brancheKatalog,
          });
          const res = await fetch(apiUrl(`risk-kpi/${contactId}/${yr}`), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error(`Year ${yr}: HTTP ${res.status}`);
        }),
      );
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore
      }
      dispatchToast(
        <Toast>
          <ToastTitle>Kennzahlen gespeichert</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      if (isEmbedded) {
        setTimeout(() => embedded?.onSuccess?.(), 600);
      } else {
        setTimeout(() => navigate(`/contacts/${contactId}`, { state: { defaultTab: "kennzahlen" } }), 800);
      }
    } catch (e) {
      console.error("Kennzahlen save failed", e);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const section = sections[currentSectionIdx] ?? sections[0];
  const accent = sectionColorHex[section.color];

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} />
      <div className={styles.stickyHeader}>
        <div className={styles.pageHeader}>
          <div className={styles.titleStack}>
            <h1 className={styles.pageTitle}>
              <span className={styles.titleChipIcon} aria-hidden="true">
                <DocumentAdd20Regular />
              </span>
              Kennzahlen erfassen
            </h1>
            <div className={styles.titleChips}>
              <span className={styles.titleChip}>Geführtes Formular</span>
              <span className={styles.yearRange}>
                {years[0]}–{years[years.length - 1]}
              </span>
            </div>
          </div>
          {!isEmbedded && (
            <Button appearance="subtle" onClick={() => navigate(-1)}>
              Zurück
            </Button>
          )}
        </div>

        <div className={styles.contextPanel}>
          <div className={styles.contextBlock}>
            <span className={styles.contextLabel}>Kontakt</span>
            <span className={styles.contextName}>{contactName || "—"}</span>
            <span className={styles.contextSub}>
              <Building20Regular style={{ verticalAlign: "middle", marginRight: 4 }} />
              {contactAddr || "—"}
            </span>
          </div>
          <div className={styles.contextBlock}>
            <span className={styles.contextLabel}>Erfasste Jahre</span>
            <span className={styles.contextSub}>
              {years.length} Jahr{years.length === 1 ? "" : "e"} · horizontal scrollbar
            </span>
          </div>
        </div>
      </div>

      {!inYearsStep && (
        <div className={styles.sectionTabs} role="tablist" aria-label="Abschnitte">
          {sections.map((s, sIdx) => {
            const active = sIdx === currentSectionIdx;
            const color = sectionColorHex[s.color];
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={mergeClasses(styles.sectionTab, active && styles.sectionTabActive)}
                style={active ? { backgroundColor: color, borderColor: color } : { color }}
                onClick={() => setCurrentSectionIdx(sIdx)}
              >
                {sIdx + 1}. {s.title}
              </button>
            );
          })}
        </div>
      )}

      {inYearsStep ? (
        <div className={styles.formPanel}>
          <h2 className={styles.title}>Wie viele Jahre zurück möchten Sie erfassen?</h2>
          <p className={styles.yearsPromptIntro}>
            Das laufende Jahr (Ist) und das Planjahr sind immer dabei. Wählen Sie zusätzlich,
            wie weit Sie historisch zurückgehen möchten.
          </p>
          <div className={styles.yearsTileGrid}>
            {[2, 3, 4, 5].map((n) => {
              const active = yearsBack === n;
              return (
                <button
                  key={n}
                  type="button"
                  className={mergeClasses(styles.yearsTile, active && styles.yearsTileActive)}
                  onClick={() => setYearsBack(n)}
                >
                  <span className={styles.yearsTileNum}>{n}</span>
                  <span className={styles.yearsTileLabel}>
                    {n === 1 ? "Jahr zurück" : "Jahre zurück"}
                  </span>
                </button>
              );
            })}
          </div>
          {yearsBack !== null && (
            <div className={styles.yearsRangePreview}>
              Erfasster Zeitraum: <strong>{years[0]}</strong> – <strong>{years[years.length - 1]}</strong>
              {" "}({years.length} Spalten)
            </div>
          )}
        </div>
      ) : (
        <div className={styles.formPanel}>
          <span className={styles.sectionPill} style={{ backgroundColor: accent }}>
            {section.title}
          </span>
          <div className={styles.titleRow}>
            <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 200 }}>
              <h2 className={styles.title}>{section.title}</h2>
              <p className={styles.description}>
                Tragen Sie zuerst „{section.gesamt.label}" für mindestens ein Jahr ein.
                {section.details.length > 0 &&
                  " Anschließend erscheinen die Detailfelder zu dieser Kennzahl."}
              </p>
            </div>
            <div className={styles.heroBox}>
              <StepHero id={section.id} />
            </div>
          </div>
          <YearScrollGrid
            years={years}
            onShiftYears={shiftYears}
            gesamt={section.gesamt}
            details={
              section.branchenVerteilung ? [...section.details, ...branchenFelder] : section.details
            }
            getValue={getValue}
            setValue={setValue}
          />
          {section.branchenVerteilung && (
            <div className={styles.brancheAuswahl}>
              {firmaBranchenIds.length === 0 && (
                <span className={styles.questionSubLabel}>
                  Für diese Firma ist keine Branche hinterlegt — Branche in der Akte pflegen.
                </span>
              )}
              <SearchableSelect
                label="+ Branche"
                name="kennzahlen-wizard-branche"
                value=""
                onChange={(v) => {
                  const id = Number(v);
                  if (!Number.isFinite(id) || id <= 0) return;
                  setHinzugefuegtBranchenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
                }}
                options={branchenOptionen}
                allowFreeText={false}
                placeholder="Branchengruppe suchen …"
              />
            </div>
          )}
          {section.questions && section.questions.length > 0 && (
            <div className={styles.questionsBlock}>
              <h3 className={styles.questionsBlockTitle}>Zusätzliche Angaben</h3>
              {section.questions.map((q, qIdx) => {
                // Globale Q&A (nicht jahresgebunden) — Storage im otherInfo
                // des ersten Jahres aus `years`. Form-Antworten leben in `answers`
                // unter `${key}_${anchorYear}`.
                const anchor = years[0];
                if (q.kind === "yesnoText" || q.kind === "yesnoWithFields") {
                  const gateVal = getValue(q.gateKey, anchor);
                  return (
                    <div key={qIdx} className={styles.questionRow}>
                      <label className={styles.questionLabel}>{q.gateLabel}</label>
                      <div className={styles.yesNoRow}>
                        {(["ja", "nein"] as const).map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            className={mergeClasses(styles.yesNoBtn, (gateVal === opt) && styles.yesNoBtnActive)}
                            onClick={() => setValue(q.gateKey, anchor, opt)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      {gateVal === "ja" && q.kind === "yesnoText" && (
                        <textarea
                          className={styles.questionTextarea}
                          placeholder={q.warumLabel ?? "Warum?"}
                          value={getValue(q.warumKey, anchor)}
                          onChange={(e) => setValue(q.warumKey, anchor, e.target.value)}
                        />
                      )}
                      {gateVal === "ja" && q.kind === "yesnoWithFields" && (
                        <div className={styles.questionSubFields}>
                          {q.fields.map((f) => (
                            <label key={f.key} className={styles.questionSubField}>
                              <span className={styles.questionSubLabel}>{f.label}</span>
                              <input
                                type="text"
                                className={styles.questionInput}
                                value={getValue(f.key, anchor)}
                                onChange={(e) => setValue(f.key, anchor, e.target.value)}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                // multiText: not used yet
                return null;
              })}
            </div>
          )}
        </div>
      )}

      <div className={styles.stickyFooter}>
        <BottomBar
          onPrev={goPrev}
          onNext={goNext}
          onSubmit={onSubmit}
          onSaveDraft={saveDraftNow}
          canPrev={canPrev}
          canNext={canNext}
          isLastStep={isSubmitStep}
          submitting={submitting}
          savingDraft={savingDraft}
          draftSavedAt={draftSavedAt}
          progressLabel={
            inYearsStep
              ? `Schritt 1 von ${totalSteps} · Jahre wählen`
              : `Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`
          }
          progressFraction={progressFraction}
          submitLabel="Kennzahlen speichern"
          submittingLabel="Wird gespeichert…"
        />
      </div>
    </div>
  );
};

export default KennzahlenSectionStepForm;
